/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  CSSProperties,
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import makeAnimated from 'react-select/animated';
import ReactSelect from 'react-select';
import { MdAdd } from 'react-icons/md';
import { IconType } from 'react-icons';

import Link from 'next/link';

import { ColorScheme } from 'styled-components';
import { Tooltip } from '@chakra-ui/react';

import { useIsMounted } from '~/shared/hooks/isMounted';

import {
  StyledSelect,
  Container,
  SelectVariant,
  AddNewArrayContainer,
} from './styles';
import { LabelText } from '../LabelText';
import { ErrorMessage } from '../ErrorMessage';
import { Button } from '../Button';

type AddNewProps = ButtonHTMLAttributes<HTMLButtonElement> &
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    tooltip?: string;
    Icon?: IconType;
    backgroundColor?: ColorScheme;
    textColor?: ColorScheme;
    borderColor?: ColorScheme;
  };

export interface BaseSelectValue<T = any> {
  value: T;
  label: string;
  metadata?: Record<string, any>;
}

export type SelectValue = BaseSelectValue<any> | BaseSelectValue<any>[] | null;

export type OnChangeSelect = (value?: SelectValue, name?: string) => void;

export interface SelectProps {
  placeholder?: string;
  label?: string;
  containerStyle?: CSSProperties;
  error?: string;
  onChange?: OnChangeSelect;
  onBlur?(): void;
  value?: SelectValue | any;
  defaultValue?: SelectValue;
  options?: SelectValue[];
  isMulti?: boolean;
  name?: string;
  isDisabled?: boolean;
  addNew?: AddNewProps | AddNewProps[];
  variant?: SelectVariant;
  showErrorMessage?: boolean;
  isSearchable?: boolean;
  hint?: ReactNode;
  minLengthToSearch?: number;
  infinityScroll?: boolean;
  labelColor?: ColorScheme;
  hideValue?: boolean;
  isClearable?: boolean;
  hideMultiValue?: boolean;
  noOptionsMessage?: string;
}

const animatedComponents = makeAnimated();

const BaseSelect: ForwardRefRenderFunction<ReactSelect, SelectProps> = (
  {
    placeholder = 'Selecione uma opção',
    label,
    containerStyle,
    error,
    value,
    hint,
    options,
    defaultValue,
    isMulti = false,
    onChange = () => {},
    onBlur = () => {},
    addNew,
    name,
    variant = 'outlined',
    isDisabled,
    showErrorMessage = true,
    hideValue = false,
    isClearable = true,
    hideMultiValue = false,
    infinityScroll,
    minLengthToSearch = 0,
    labelColor,
    noOptionsMessage = 'Sem opções!',
  },
  ref
) => {
  const itemsPerScroll = 10;
  const [page, setPage] = useState(0);
  const [filteredOptions, setFilteredOptions] = useState([]);

  const isMounted = useIsMounted();

  useEffect(() => {
    if (options) {
      if (infinityScroll) {
        setPage(1);
      } else {
        setFilteredOptions(options);
      }
    }
  }, [infinityScroll, options]);

  useEffect(() => {
    if (infinityScroll && page > 0) {
      const prevPage = itemsPerScroll * (page - 1);
      const currentPage = itemsPerScroll * page;

      const newOptions = options.slice(prevPage, currentPage);
      setFilteredOptions(newOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, options]);

  const handleMenuScrollToBottom = (): void => {
    setPage((prev) => prev + 1);
  };

  const loadOptions = (inputValue, callback): void => {
    if (inputValue.length >= minLengthToSearch) {
      const optionsFiltered = options?.filter((opt) => {
        const labelFormatted = (opt as BaseSelectValue).label
          .toLowerCase()
          .replace(/\W/g, '');
        const inputValueFormatted = inputValue.toLowerCase().replace(/\W/g, '');

        return labelFormatted?.includes(inputValueFormatted);
      });

      callback(optionsFiltered);
    }
  };

  if (!isMounted) return null;

  return (
    <Container
      id="select-container"
      style={containerStyle}
      className="select-container"
      addNew={!addNew ? 'none' : Array.isArray(addNew) ? 'multi' : 'single'}
    >
      <div>
        {!!label && (
          <LabelText tooltip={hint} color={labelColor}>
            {label}
          </LabelText>
        )}

        <StyledSelect
          variant={variant}
          placeholder={placeholder}
          hasError={!!error}
          classNamePrefix="react-select"
          defaultValue={defaultValue}
          onChange={(v: SelectValue) => onChange(v, name)}
          controlShouldRenderValue={!hideMultiValue}
          value={
            hideValue
              ? null
              : typeof value === 'string' ||
                typeof value === 'boolean' ||
                typeof value === 'number'
              ? filteredOptions?.find((option) => option.value === value)
              : Array.isArray(value)
              ? value.map((item) =>
                  typeof item === 'string'
                    ? filteredOptions?.find((option) => option.value === item)
                    : item
                )
              : value
          }
          loadOptions={loadOptions}
          defaultOptions={filteredOptions}
          onBlur={onBlur}
          ref={ref}
          {...(isMulti && {
            isMulti,
            closeMenuOnSelect: false,
            components: animatedComponents,
          })}
          menuPortalTarget={document.body}
          maxMenuHeight={280}
          isClearable={isClearable}
          closeMenuOnScroll={(e) => e.target.id === 'main-element'}
          isDisabled={isDisabled}
          noOptionsMessage={() => noOptionsMessage}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          menuPosition="absolute"
          menuPlacement="auto"
          menuShouldScrollIntoView
          onMenuScrollToBottom={() => handleMenuScrollToBottom()}
          loadingMessage={({ inputValue }) =>
            inputValue.length < minLengthToSearch
              ? `Digite no mínimo ${minLengthToSearch} caracteres`
              : 'Pesquisando...'
          }
        />

        <ErrorMessage>{showErrorMessage && error}</ErrorMessage>
      </div>

      {addNew &&
        (Array.isArray(addNew) ? (
          <AddNewArrayContainer>
            {(addNew as AddNewProps[]).map(({ Icon, ..._addNew }, index) => (
              // @ts-ignore
              <Tooltip
                label={_addNew.tooltip || 'Adicionar novo'}
                hasArrow
                key={String(index)}
              >
                <div>
                  {_addNew.href ? (
                    <Link href={_addNew.href} {..._addNew} passHref>
                      <Button
                        type="button"
                        backgroundColor={_addNew.backgroundColor || 'info'}
                        textColor={_addNew.textColor || 'onInfo'}
                        borderColor={_addNew.borderColor || 'info'}
                        {..._addNew}
                      >
                        {Icon ? <Icon /> : <MdAdd />}
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      type="button"
                      backgroundColor="info"
                      textColor="onInfo"
                      {..._addNew}
                    >
                      {Icon ? <Icon /> : <MdAdd />}
                    </Button>
                  )}
                </div>
              </Tooltip>
            ))}
          </AddNewArrayContainer>
        ) : (
          // @ts-ignore
          <Tooltip label={addNew.tooltip || 'Adicionar novo'} hasArrow>
            <div>
              {addNew.href ? (
                <Link href={addNew.href} passHref {...addNew}>
                  <Button
                    type="button"
                    backgroundColor="info"
                    textColor="onInfo"
                    {...addNew}
                  >
                    {addNew.Icon ? <addNew.Icon /> : <MdAdd />}
                  </Button>
                </Link>
              ) : (
                <Button
                  type="button"
                  backgroundColor="info"
                  textColor="onInfo"
                  {...addNew}
                >
                  {addNew.Icon ? <addNew.Icon /> : <MdAdd />}
                </Button>
              )}
            </div>
          </Tooltip>
        ))}
    </Container>
  );
};

export const Select = forwardRef(BaseSelect);
